import { z } from "zod";
import { Amount, CostMonth } from "./amounts.js";

/*
 * Budgets (docs/budgets.md, owner's decision of 2026-10-20): a monthly
 * amount per project, excluding VAT, set by administrators and kept per
 * month. Two rules, for everyone but administrators (who are warned):
 * - a NEW paid service (server, volume, IP, floating IP, snapshot, load
 *   balancer) is refused once the month's forecast would reach the budget;
 * - a change to a service that EXISTS (bigger type, backups, bigger volume,
 *   IPv4 for a server) may go up to the budget plus the change margin
 *   («Запас на изменения», 10 % by default), for urgent small changes.
 * Nothing is ever stopped or deleted automatically.
 */

/** Percentages that notify, on the forecast and on the actual. */
export const DEFAULT_THRESHOLDS = [50, 80, 100] as const;
/** «Запас на изменения» of a new organisation, in %. */
export const DEFAULT_CHANGE_MARGIN = 10;

/** A positive amount of money with at most two decimals (`"250"`, `"99.90"`). */
export const BudgetAmount = z
  .string()
  .trim()
  .regex(/^\d{1,9}(\.\d{1,2})?$/, "An amount like 250 or 99.90")
  .refine((v) => Number(v) > 0, "More than zero");

/** «Запас на изменения»: how far above the budget changes to existing services may go, in %. */
export const ChangeMargin = z.number().int().min(0).max(100);

export const BudgetThresholds = z
  .array(z.number().int().min(1).max(200))
  .min(1)
  .max(5)
  .refine((v) => new Set(v).size === v.length, "Each threshold once");

export const Person = z.object({ id: z.uuid(), name: z.string() });
export type Person = z.infer<typeof Person>;

/** One version of a project's budget, valid from a month until the next version. */
export const BudgetVersion = z.object({
  validFrom: CostMonth,
  /** Null: no budget from that month on. */
  monthlyAmount: Amount.nullable(),
  /** Null: the organisation's default margin. */
  changeMargin: z.number().int().nullable(),
  thresholds: z.array(z.number().int()),
  updatedAt: z.iso.datetime(),
  updatedBy: Person.nullable(),
});
export type BudgetVersion = z.infer<typeof BudgetVersion>;

/** A month's budget and how much of it is used, actually and by the forecast. */
export const BudgetUse = z.object({
  /** The month's budget: its monthly amount plus approved one-time raises. */
  amount: Amount,
  monthlyAmount: Amount,
  /** Approved one-time raises of the month. */
  raised: Amount,
  /** The margin in % that applies (the project's own or the organisation's). */
  changeMargin: z.number().int(),
  /** Budget × (1 + margin): how far changes to existing services may take the forecast. */
  changeLimit: Amount,
  thresholds: z.array(z.number().int()),
  usedPercent: z.number(),
  forecastPercent: z.number().nullable(),
  /** The forecast (the whole month, for past months) has reached the budget: no new services. */
  exceeded: z.boolean(),
});
export type BudgetUse = z.infer<typeof BudgetUse>;

export const RaiseKind = z.enum(["once", "permanent"]);
export type RaiseKind = z.infer<typeof RaiseKind>;

export const RaiseStatus = z.enum(["pending", "approved", "rejected"]);
export type RaiseStatus = z.infer<typeof RaiseStatus>;

/** A project owner's request for more budget: this month only, or from now on. */
export const BudgetRaise = z.object({
  id: z.uuid(),
  projectId: z.uuid(),
  projectName: z.string(),
  /** The month it was asked for (a one-time raise counts in that month only). */
  month: CostMonth,
  /** By how much. */
  amount: Amount,
  kind: RaiseKind,
  reason: z.string(),
  status: RaiseStatus,
  requestedBy: Person.nullable(),
  requestedAt: z.iso.datetime(),
  decidedBy: Person.nullable(),
  decidedAt: z.iso.datetime().nullable(),
  note: z.string().nullable(),
});
export type BudgetRaise = z.infer<typeof BudgetRaise>;

export const AlertBasis = z.enum(["actual", "forecast"]);
export type AlertBasis = z.infer<typeof AlertBasis>;

/** A threshold reached in a month; each is notified once per month and basis. */
export const BudgetAlert = z.object({
  month: CostMonth,
  threshold: z.number().int(),
  basis: AlertBasis,
  sentAt: z.iso.datetime(),
});
export type BudgetAlert = z.infer<typeof BudgetAlert>;

/** `GET /projects/:projectId/budget`: this month's budget, its history, raises, alerts. */
export const ProjectBudget = z.object({
  projectId: z.uuid(),
  month: CostMonth,
  currency: z.string().length(3),
  /** The version that applies this month; null without a budget. */
  budget: BudgetVersion.nullable(),
  use: BudgetUse.nullable(),
  /** The margin a budget without its own takes (the organisation's). */
  defaultChangeMargin: z.number().int(),
  /** Every version, newest first. */
  history: z.array(BudgetVersion),
  /** Newest first. */
  raises: z.array(BudgetRaise),
  /** This month's, newest first. */
  alerts: z.array(BudgetAlert),
});
export type ProjectBudget = z.infer<typeof ProjectBudget>;

/**
 * `PUT /projects/:projectId/budget`: the budget from this month on;
 * `monthlyAmount: null` removes it, `changeMargin: null` takes the
 * organisation's default. Earlier months keep the budget they had.
 */
export const SetBudgetRequest = z.object({
  monthlyAmount: BudgetAmount.nullable(),
  changeMargin: ChangeMargin.nullable().optional(),
  thresholds: BudgetThresholds.optional(),
});
export type SetBudgetRequest = z.input<typeof SetBudgetRequest>;

export const RequestRaiseRequest = z.object({
  amount: BudgetAmount,
  kind: RaiseKind,
  reason: z.string().trim().min(1).max(500),
});
export type RequestRaiseRequest = z.input<typeof RequestRaiseRequest>;

export const DecideRaiseRequest = z.object({ note: z.string().trim().max(500).optional() });
export type DecideRaiseRequest = z.input<typeof DecideRaiseRequest>;

/** `GET|PUT /budgets/settings`: the organisation's default margin. */
export const BudgetSettings = z.object({ defaultChangeMargin: ChangeMargin });
export type BudgetSettings = z.infer<typeof BudgetSettings>;

/** Which rule a change falls under: a new paid service, or a change to one that exists. */
export const BudgetRule = z.enum(["new", "change"]);
export type BudgetRule = z.infer<typeof BudgetRule>;

/** Part of the `budget_exceeded` problem: which rule refused and by how much. */
export const BudgetExcess = z.object({
  currency: z.string().length(3),
  rule: BudgetRule,
  budget: Amount,
  /** The budget for a new service; budget × (1 + margin) for a change. */
  limit: Amount,
  changeMargin: z.number().int(),
  forecast: Amount,
  forecastAfter: Amount,
  /** forecastAfter − limit. */
  over: Amount,
  /** What the change adds per month at full price. */
  monthly: Amount,
});
export type BudgetExcess = z.infer<typeof BudgetExcess>;

/** The budget check of a change, in `POST …/costs/preview`. */
export const BudgetCheck = z.object({
  rule: BudgetRule,
  budget: Amount,
  limit: Amount,
  changeMargin: z.number().int(),
  over: Amount,
  /** The forecast has reached the budget already: no new services. */
  exceeded: z.boolean(),
  /** The change breaks its rule. */
  exceeds: z.boolean(),
  /** The API would refuse it for the caller (not an administrator). */
  blocked: z.boolean(),
});
export type BudgetCheck = z.infer<typeof BudgetCheck>;
